// src/components/LeadForm.js
// Reusable lead-capture form. Writes real leads to Firestore `leads` collection.
// Used by the Contact page and the NewsletterSection (Home / About).
import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaRegPaperPlane } from 'react-icons/fa';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const N  = '#0d2235';
const E  = '#29ABE2';
const Eo = '#1a8fc0';
const PA = '#ffffff';
const MT = '#5a7a8f';
const BR = 'rgba(13,34,53,0.11)';
const GREEN = '#16c784';
const RED = '#ea3943';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
`;

const Card = styled.div`
  background: rgba(255,255,255,0.88);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border: 1px solid ${BR};
  border-radius: 22px;
  box-shadow: 0 24px 60px -22px rgba(13,34,53,0.30), inset 0 1px 0 rgba(255,255,255,0.9);
  padding: 30px 28px;

  @media (max-width: 560px) { padding: 24px 20px; }
`;

const Field = styled.div`
  margin-bottom: 1rem;

  label { display: block; font-size: 0.84rem; font-weight: 700; color: ${N}; margin-bottom: 0.4rem; }
  label .req { color: ${RED}; }

  input, textarea {
    width: 100%;
    font: inherit;
    font-size: 0.95rem;
    color: ${N};
    background: ${PA};
    border: 1.5px solid ${BR};
    border-radius: 12px;
    padding: 12px 14px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    outline: none;
    resize: vertical;

    &::placeholder { color: #9bb0bf; }
    &:focus { border-color: ${E}; box-shadow: 0 0 0 3px rgba(41,171,226,0.16); }
  }
  textarea { min-height: 104px; }
`;

const Consent = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.83rem;
  color: ${MT};
  line-height: 1.5;
  margin: 0.3rem 0 1.1rem;
  cursor: pointer;

  input { width: 18px; height: 18px; margin-top: 1px; accent-color: ${E}; flex-shrink: 0; cursor: pointer; }
`;

const Submit = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 14px 22px;
  border: none;
  border-radius: 12px;
  background: ${E};
  color: ${PA};
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.14s ease, opacity 0.14s ease, background 0.14s ease;

  &:hover:not(:disabled) { transform: translateY(-1px); background: ${Eo}; }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { opacity: 0.65; cursor: not-allowed; }

  svg { font-size: 1.05rem; }
`;

const Note = styled.p`
  font-size: 0.78rem;
  color: ${MT};
  text-align: center;
  margin: 0.9rem 0 0;
  svg { color: ${GREEN}; }
`;

const Alert = styled.div`
  font-size: 0.86rem;
  font-weight: 600;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 1rem;
  background: rgba(234,57,67,0.10);
  color: ${RED};
  border: 1px solid rgba(234,57,67,0.25);
`;

const Success = styled(motion.div)`
  text-align: center;
  padding: 14px 6px;
  animation: ${popIn} 0.4s ease both;

  .badge {
    width: 62px; height: 62px; border-radius: 50%; margin: 0 auto 1rem;
    display: flex; align-items: center; justify-content: center;
    background: rgba(22,199,132,0.14); color: ${GREEN}; font-size: 1.8rem;
  }
  h3 { font-size: 1.35rem !important; font-weight: 800; color: ${N} !important; margin: 0 0 0.5rem; }
  p  { font-size: 0.94rem; color: ${MT} !important; line-height: 1.6; margin: 0 auto; max-width: 340px; }
`;

function LeadForm({ source = 'website', compact = false, ctaLabel = 'Get insights & subscribe' }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(true);
  const [website, setWebsite] = useState(''); // honeypot
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [err, setErr] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');

    if (website) { setStatus('success'); return; } // bot trap

    const cleanEmail = email.trim().toLowerCase();
    if (!EMAIL_RE.test(cleanEmail)) {
      setErr('Please enter a valid email address.');
      return;
    }

    setStatus('submitting');
    try {
      await addDoc(collection(db, 'leads'), {
        name: name.trim(),
        email: cleanEmail,
        message: message.trim(),
        newsletter: consent,
        source,
        status: 'new',
        page: typeof window !== 'undefined' ? window.location.pathname : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        createdAt: serverTimestamp(),
      });
      setStatus('success');
    } catch (e2) {
      console.error('Lead submission failed:', e2);
      setStatus('error');
      setErr('Something went wrong on our end. Please try again in a moment.');
    }
  };

  if (status === 'success') {
    return (
      <Card>
        <Success initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
          <div className="badge"><FaCheckCircle /></div>
          <h3>You're on the list! 🎉</h3>
          <p>Thanks! We've saved your details and will be in touch with AI crypto insights soon. Keep an eye on your inbox.</p>
        </Success>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={handleSubmit} noValidate>
        {status === 'error' && <Alert>{err}</Alert>}

        <Field>
          <label htmlFor={`lf-name-${source}`}>Name</label>
          <input
            id={`lf-name-${source}`} type="text" autoComplete="name"
            placeholder="Your name (optional)"
            value={name} onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <Field>
          <label htmlFor={`lf-email-${source}`}>Email <span className="req">*</span></label>
          <input
            id={`lf-email-${source}`} type="email" autoComplete="email" required
            placeholder="you@email.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (err) setErr(''); }}
          />
        </Field>

        {!compact && (
          <Field>
            <label htmlFor={`lf-msg-${source}`}>Message</label>
            <textarea
              id={`lf-msg-${source}`}
              placeholder="Anything you'd like to tell us? (optional)"
              value={message} onChange={(e) => setMessage(e.target.value)}
            />
          </Field>
        )}

        {/* honeypot — hidden from humans, catches bots */}
        <input
          type="text" tabIndex="-1" autoComplete="off"
          value={website} onChange={(e) => setWebsite(e.target.value)}
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
          aria-hidden="true"
        />

        <Consent>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>Subscribe me to the Trend Genie newsletter for AI crypto market insights. I can unsubscribe anytime.</span>
        </Consent>

        <Submit type="submit" disabled={status === 'submitting'}>
          {status === 'submitting'
            ? 'Sending…'
            : <><FaRegPaperPlane /> {ctaLabel}</>}
        </Submit>

        <Note><FaCheckCircle /> Free · No spam · Unsubscribe anytime</Note>
      </form>
    </Card>
  );
}

export default LeadForm;
